<template>

  <v-container fluid class="route-details" v-if="route">

    <!-- Title: Route Details -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pt-0">
        <span class="title">Route Details</span>
      </v-col>
    </v-row>

    <!-- Completed time -->
    <v-row class="pa-0 ma-0 mt-2" v-if="routeStatus === 'COMPLETED'">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Completed:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11" class="d-flex align-center">
        <div class="pr-5 scheduled-date">{{ formatDate(route.completed_at) }}</div>
        <div class="grey--text scheduled-time">{{ formatTime(route.completed_at) }}</div>
        <div class="pl-2 grey--text scheduled-time">{{ formatTimezone(route.completed_at) }}</div>
      </v-col>
    </v-row>

    <!-- Canceled time -->
    <v-row class="pa-0 ma-0 mt-2" v-if="routeStatus === 'CANCELED'">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Canceled:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11" class="d-flex align-center">
        <div class="pr-5 scheduled-date">{{ formatDate(route.canceled_at) }}</div>
        <div class="grey--text scheduled-time">{{ formatTime(route.canceled_at) }}</div>
        <div class="pl-2 grey--text scheduled-time">{{ formatTimezone(route.canceled_at) }}</div>
      </v-col>
    </v-row>

    <!-- Started time -->
    <v-row class="pa-0 ma-0 mt-2" v-if="routeStatus === 'ON_DELIVERY' || routeStatus === 'RETURNING_TO_HQ'">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Started:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11" class="d-flex align-center">
        <div class="pr-5 scheduled-date">{{ formatDate(route.on_delivery_at) }}</div>
        <div class="grey--text scheduled-time">{{ formatTime(route.on_delivery_at) }}</div>
        <div class="pl-2 grey--text scheduled-time">{{ formatTimezone(route.on_delivery_at) }}</div>
      </v-col>
    </v-row>

    <!-- Scheduled time -->
    <v-row class="pa-0 ma-0 mt-2 d-flex align-center">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Schedule:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11" class="d-flex align-center">
        <div class="pr-5 scheduled-date">{{ formatDate(route.scheduled_start_time) }}</div>
        <div class="grey--text scheduled-time">{{ formatTime(route.scheduled_start_time) }}</div>
        <div class="px-3 grey--text scheduled-time">-</div>
        <div class="grey--text scheduled-time">{{ formatTime(route.estimated_return_time) }}</div>
        <div class="pl-2 grey--text scheduled-time">{{ formatTimezone(route.estimated_return_time) }}</div>
      </v-col>
    </v-row>

    <!-- Vehicle Lot -->
    <v-row class="pa-0 ma-0 mt-3 d-flex align-center">
      <v-col cols="12" xs="12" sm="3" md="2" lg="1">
        <div class="attr-label">Vehicle Lot:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="9" md="10" lg="11">
        <router-link :to="generateVehicleLotPageLink()">
          <span class="vehicle-lot-label">{{ vehicleLot.name }}</span>
        </router-link>
      </v-col>
    </v-row>

    <!-- Vehicle -->
    <v-row class="pa-0 ma-0 d-flex align-center justify-center" :class="(newVehicleForChange) ? 'change-outline' : ''">
      <v-col cols="12" xs="12" sm="3" md="2" lg="1">
        <div class="attr-label">Vehicle:</div>
      </v-col>
      <v-col cols="12" xs="12" :sm="(canChangeVehicle) ? 5 : 9" :md="(canChangeVehicle) ? 3 : 10" :lg="(canChangeVehicle) ? 3 : 11">
        <router-link :to="generateVehiclePageLink(vehicle)">
          <span class="vehicle-label">{{ vehicle.license_plate }}</span>
        </router-link>
      </v-col>
      <v-col cols="12" xs="12" sm="4" md="7" lg="8" class="" v-if="canChangeVehicle">
        <template v-if="newVehicleForChange">
          <div class="mt-1">
            <span class="attr-label mr-2">Change to:</span>
            <router-link :to="generateVehiclePageLink(newVehicleForChange)">{{ newVehicleForChange.license_plate }}</router-link>
          </div>
          <div class="mt-3 d-flex align-center" v-if="allowStatusChangeForReplacedVehicle">
            <div class="pr-3">Select new status for {{ vehicle.license_plate }}:</div>
            <div style="max-width:180px;">
              <v-select
                dense
                outlined
                hide-details
                :items="availableVehicleStatusChangeOptions"
                item-text="label"
                item-value="value"
                v-model="replacedVehicleStatusForChange" />
            </div>
          </div>
          <div class="mt-4 mb-2">
            <v-btn
                small
                color="primary"
                :disabled="allowStatusChangeForReplacedVehicle && !replacedVehicleStatusForChange"
                @click="performChangeVehicle">
              Make Change
            </v-btn>
            <v-btn
                plain
                small
                class="ml-8"
                color="primary"
                @click="cancelChangeVehicle">
              Cancel
            </v-btn>
          </div>
        </template>
        <template v-else>
          <v-btn
              small
              color="orange darken-3"
              @click="enableChangeVehicle">
            Change Vehicle
          </v-btn>
        </template>
      </v-col>
    </v-row>

    <!-- Driver -->
    <v-row class="pa-0 ma-0 d-flex align-center justify-center" :class="(newDriverForChange) ? 'change-outline' : ''">
      <v-col cols="12" xs="12" sm="3" md="2" lg="1">
        <div class="attr-label">Driver:</div>
      </v-col>
      <v-col cols="12" xs="12" :sm="(canChangeDriver) ? 5 : 9" :md="(canChangeDriver) ? 3 : 10" :lg="(canChangeDriver) ? 3 : 11">
        <div class="driver-label">{{ driverName }}</div>
      </v-col>
      <v-col cols="12" xs="12" sm="4" md="7" lg="8" class="" v-if="canChangeDriver">
        <template v-if="newDriverForChange">
          <div class="mt-1">
            <span class="attr-label mr-2">Change to:</span>
            <span>{{ newDriverForChange.first_name }} {{ newDriverForChange.last_name }}</span>
          </div>
          <div class="mt-3 d-flex align-center" v-if="allowStatusChangeForReplacedDriver">
            <div class="pr-3">Select new status for {{ driverName }}:</div>
            <div style="max-width:180px;">
              <v-select
                dense
                outlined
                hide-details
                :items="availableDriverStatusChangeOptions"
                item-text="label"
                item-value="value"
                v-model="replacedDriverStatusForChange" />
            </div>
          </div>
          <div class="mt-4 mb-2">
            <v-btn
                small
                color="primary"
                @click="performChangeDriver">
              Make Change
            </v-btn>
            <v-btn
                plain
                small
                class="ml-8"
                color="primary"
                @click="cancelChangeDriver">
              Cancel
            </v-btn>
          </div>
        </template>
        <template v-else>
          <v-btn
              small
              color="orange darken-3"
              @click="enableChangeDriver">
            Change Driver
          </v-btn>
        </template>
      </v-col>
    </v-row>

    <!-- Route Type -->
    <v-row class="pa-0 ma-0 mt-5">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Route Type:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11">
        <div class="route-type">{{ routeType }}</div>
      </v-col>
    </v-row>

    <!-- Route Ref Number -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Route Ref:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11">
        <div class="route-ref-number">{{ formatTrackingNumber }}</div>
      </v-col>
    </v-row>

    <!-- Dispatched Date/Time -->
    <v-row class="pa-0 ma-0 mt-5">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Dispatched:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11" class="d-flex align-center">
        <div>{{ formatDate(route.dispatched_at) }}</div>
        <div class="grey--text ml-3">{{ formatTime(route.dispatched_at) }} {{ formatTimezone(route.dispatched_at) }}</div>
      </v-col>
    </v-row>

    <!-- Dispatcher -->
    <v-row class="pa-0 ma-0">
      <v-col cols="12" xs="12" sm="12" md="2" lg="1">
        <div class="attr-label">Dispatcher:</div>
      </v-col>
      <v-col cols="12" xs="12" sm="12" md="10" lg="11">
        <div>{{ dispatcherName }}</div>
      </v-col>
    </v-row>

    <!--------------------------->
    <!-- Change Vehicle Dialog -->
    <!--------------------------->
    <v-dialog persistent fullscreen v-model="isChangeVehicleDialogOpen">
      <vehicle-status-selector
          :activeSelected="vehicle"
          v-on:canceled="isChangeVehicleDialogOpen = false"
          v-on:selected="handleSelectedVehicleChange" />
    </v-dialog>

    <!-------------------------->
    <!-- Change Driver Dialog -->
    <!-------------------------->
    <v-dialog persistent fullscreen v-model="isChangeDriverDialogOpen">
      <driver-status-selector
          :activeSelected="driver"
          v-on:canceled="isChangeDriverDialogOpen = false"
          v-on:selected="handleSelectedDriverChange" />
    </v-dialog>

  </v-container>

</template>

<script>
import VehicleStatusSelector from '@/components/vehicles/VehicleStatusSelector'
import DriverStatusSelector from '@/components/drivers/DriverStatusSelector'

export default {
  name: 'RouteDetails',

  components: {
    VehicleStatusSelector,
    DriverStatusSelector
  },

  props: {
    route: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      isProcessing: false,
      processingError: null,
      // ----- vehicle change management
      isChangeVehicleDialogOpen: false,
      newVehicleForChange: null,
      allowStatusChangeForReplacedVehicle: false,
      replacedVehicleStatusForChange: null,
      // ----- driver change management
      isChangeDriverDialogOpen: false,
      newDriverForChange: null,
      allowStatusChangeForReplacedDriver: false,
      replacedDriverStatusForChange: null
    }
  },

  computed: {
    dispatcher () {
      return this.route.dispatcher || {}
    },
    dispatcherName () {
      const firstName = this.dispatcher.first_name
      const lastName = this.dispatcher.last_name
      return (lastName) ? `${firstName} ${lastName}` : firstName
    },
    isManualMode () {
      return this.route.route_info.manual === true
    },
    routeStatus () {
      return this.route.route_status
    },
    routeType () {
      return (this.isManualMode) ? 'Manual' : 'Optimized'
    },
    vehicleLot () {
      return this.route.vehicle_lot || {}
    },
    vehicle () {
      return this.route.vehicle || {}
    },
    availableVehicleStatusChangeOptions () {
      return [
        { label: 'Returning to HQ', value: 'RETURNING_TO_HQ' },
        { label: 'At HQ (Ready)', value: 'AT_HQ' },
        { label: 'In Maintenance', value: 'IN_MAINTENANCE' },
        { label: 'Retire', value: 'RETIRED' }
      ]
    },
    driver () {
      return this.route.driver || {}
    },
    driverName () {
      return (this.route.driver)
        ? `${this.route.driver.first_name} ${this.route.driver.last_name}`
        : null
    },
    availableDriverStatusChangeOptions () {
      // TODO !!!
      return [
        // { label: 'Returning to HQ', value: 'RETURNING_TO_HQ' },
        // { label: 'At HQ (Ready)', value: 'AT_HQ' },
        // { label: 'In Maintenance', value: 'IN_MAINTENANCE' },
        // { label: 'Retire', value: 'RETIRED' }
      ]
    },
    formatTrackingNumber () {
      const data = (this.route.tracking_number) ? this.route.tracking_number.match(/.{1,4}/g) : null
      return (data) ? data.join(' ') : null
    },
    canChangeDriver () {
      return this.routeStatus === 'DISPATCHED' &&
        (this.$root.activeUserHasRole('admin') || this.$root.activeUserHasRole('dispatcher'))
    },
    canChangeVehicle () {
      return this.routeStatus === 'DISPATCHED' &&
        (this.$root.activeUserHasRole('admin') || this.$root.activeUserHasRole('dispatcher'))
    }
  },

  methods: {
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'dddd MMM D, YYYY') : ''
    },

    formatTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'h:mm a') : ''
    },

    formatTimezone (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'z') : ''
    },

    generateVehicleLotPageLink () {
      return {
        name: 'vehicle-lot-item'
      }
    },

    generateVehiclePageLink (vehicle) {
      return {
        name: 'vehicle-item',
        params: { vehicleId: vehicle.id }
      }
    },

    enableChangeVehicle () {
      this.isChangeVehicleDialogOpen = true
    },

    cancelChangeVehicle () {
      this.isChangeVehicleDialogOpen = false
      this.replacedVehicleStatusForChange = null
      this.newVehicleForChange = null
    },

    handleSelectedVehicleChange (newVehicle) {
      this.isChangeVehicleDialogOpen = false
      if (!newVehicle) return false
      if (newVehicle.id !== this.vehicle.id) this.newVehicleForChange = newVehicle
    },

    enableChangeDriver () {
      this.isChangeDriverDialogOpen = true
    },

    cancelChangeDriver () {
      this.isChangeDriverDialogOpen = false
      this.replacedDriverStatusForChange = null
      this.newDriverForChange = null
    },

    handleSelectedDriverChange (newDriver) {
      this.isChangeDriverDialogOpen = false
      if (!newDriver) return false
      if (newDriver.id !== this.driver.id) this.newDriverForChange = newDriver
    },

    async performChangeVehicle () {
      if (!this.canChangeVehicle || (this.allowStatusChangeForReplacedVehicle && !this.newVehicleForChange)) return false

      try {
        this.isProcessing = true

        const bodyData = {
          vehicle_id: this.newVehicleForChange.id
        }
        if (this.newVehicleForChange) bodyData.replaced_vehicle_status = this.replacedVehicleStatusForChange

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/change-vehicle`,
          opts: {
            bodyData
          }
        })

        // Emit event
        this.$emit('updated', { action: 'changed-vehicle' })
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    },

    async performChangeDriver () {
      if (!this.canChangeDriver || (this.allowStatusChangeForReplacedDriver && !this.newDriverForChange)) return false

      try {
        this.isProcessing = true

        const bodyData = {
          user_id: this.newDriverForChange.id
        }
        if (this.newDriverForChange) bodyData.replaced_driver_status = this.replacedDriverStatusForChange

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.route.id}/change-driver`,
          opts: {
            bodyData
          }
        })

        // Emit event
        this.$emit('updated', { action: 'changed-driver' })
      } catch (err) {
        this.processingError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style>
  .route-details .attr-label {
    color: #9c9c9c;
    font-weight: 400;
  }

  .route-details .scheduled-date {
    font-size: 1.14rem;
  }

  .route-details .scheduled-time {
    font-size: 1.14rem;
  }

  /* .route-details .vehicle-lot-label {
    font-size: 1.14rem;
  } */

  /* .route-details .driver-label {
    font-size: 1.14rem;
  } */

  /* .route-details .vehicle-label {
    font-size: 1.14rem;
  } */

  .route-details .route-type {
    text-transform:uppercase !important;
    color: #727272;
  }

  .route-details .route-ref-number {
    font-size: 1.1rem;
    letter-spacing: 1.3px;
  }

  .route-details .change-outline {
    border: 1px solid #F17826;
    border-radius: 4px;
    margin-top: 10px !important;
    margin-bottom: 14px !important;
  }

  .route-details a {
    text-decoration: none;
  }
  .route-details a:hover {
    text-decoration: underline;
  }
</style>
